import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  IconButton,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
} from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import useHttp from "../../Hooks/use-http";
import { useHistory } from "react-router-dom";
import Loading from "../../Loading/Loading";
import SearchFilter from "../CommonComponent/SearchFilter";
import CommonTablePagination from "../CommonComponent/CommonTablePagination";
import VerifiedIcon from "@mui/icons-material/Verified";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { PAGINATIONBASEVALUE } from "../../Constant";
import PortalTitle from "../CommonComponent/PortalTitle";
import CustomInfoIcon from "../CommonComponent/CustomInfoIcon";

// Updated headers structure with both label and key
const headers = [
  { label: "Partner Details", key: "fullName" },
  { label: "Vehicle", key: "vehicles" },
  { label: "Driver", key: "drivers" },
  { label: "City", key: "city" },
  { label: "Country", key: "country" },
  { label: "Status", key: "isSuspended" },
  { label: "Action", key: "action" },
];

const headerWidths = {
  "Partner Details": "20%",
  Vehicle: "25%",
  Driver: "25%",
  City: "10%",
  Country: "10%",
  Status: "5%",
  Action: "5%",
};

// Helper function for stable sorting
function stableSort(array, comparator) {
  if (!array) return [];
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function descendingComparator(a, b, orderBy) {
  const valueA = getOrderValue(a, orderBy);
  const valueB = getOrderValue(b, orderBy);

  if (valueB < valueA) return -1;
  if (valueB > valueA) return 1;
  return 0;
}

function getOrderValue(item, key) {
  // Handle status field specially
  if (key === "isSuspended") {
    return item.isSuspended ? "Not Approved" : "Approved";
  }
  const value = item[key] || "";
  return typeof value === "string" ? value.toLowerCase() : value;
}

const Partners = () => {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("fullName");
  const [partnerData, setPartnerData] = useState([]);
  const { sendRequest, isLoading } = useHttp();
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGINATIONBASEVALUE);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [filteredData, setFilteredData] = useState(null);
  const [search, setSearch] = useState("");
  const [isApiCall, setIsApiCall] = useState(false);
  const [filteredPartnerData, setFilteredPartnerData] = useState([]);

  useEffect(() => {
    sendRequest(
      {
        url: "/partners",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          emailID: localStorage.getItem("user"),
        },
      },
      partnerList
    );
  }, [sendRequest, isApiCall]);

  const partnerList = (data) => {
    setPartnerData(data?.partners || []);
  };

  // Search filter effect
  useEffect(() => {
    const query = search.toLowerCase().trim();
    
    let filteredPartners = partnerData;
    
    if (query) {
      filteredPartners = partnerData.filter((partner) => {
        // Search in partner fields
        const partnerEmailMatch = String(partner.emailID || "").toLowerCase().includes(query);
        const partnerMobileMatch = String(partner.mobileNumber || "").toLowerCase().includes(query);
        const partnerNameMatch = String(partner.fullName || "").toLowerCase().includes(query);
        
        // Search in vehicle fields
        const vehicleMatch = partner.vehicles?.some((vehicle) => {
          return (
            String(vehicle.maker || "").toLowerCase().includes(query) ||
            String(vehicle.model || "").toLowerCase().includes(query) ||
            String(vehicle.regNumber || "").toLowerCase().includes(query) ||
            String(vehicle.color || "").toLowerCase().includes(query) ||
            String(vehicle.fuelType || "").toLowerCase().includes(query) ||
            String(vehicle.vehicleType || "").toLowerCase().includes(query)
          );
        });
        
        // Search in driver fields
        const driverMatch = partner.drivers?.some((driver) => {
          const driverFullName = driver.fullName || `${driver.fName || ""} ${driver.lName || ""}`.trim();
          return (
            String(driverFullName).toLowerCase().includes(query) ||
            String(driver.fName || "").toLowerCase().includes(query) ||
            String(driver.lName || "").toLowerCase().includes(query) ||
            String(driver.emailID || "").toLowerCase().includes(query) ||
            String(driver.mobileNumber || "").toLowerCase().includes(query)
          );
        });
        
        return partnerEmailMatch || partnerMobileMatch || partnerNameMatch || vehicleMatch || driverMatch;
      });
    }
    
    // Store filtered data for pagination
    setFilteredPartnerData(filteredPartners);
    
    // Reset to first page when search changes
    if (search) {
      setPage(0);
    }
  }, [partnerData, search]);

  // Sorting and pagination effect
  useEffect(() => {
    const dataToUse = search ? filteredPartnerData : partnerData;
    
    // Apply sorting
    const sortedData = stableSort(
      dataToUse,
      order === "asc"
        ? (a, b) => ascendingComparator(a, b, orderBy)
        : (a, b) => descendingComparator(a, b, orderBy)
    );

    // Apply pagination
    const paginatedData = sortedData?.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    setFilteredData(paginatedData);
  }, [filteredPartnerData, partnerData, search, order, orderBy, page, rowsPerPage]);

  function ascendingComparator(a, b, orderBy) {
    const valueA = getOrderValue(a, orderBy);
    const valueB = getOrderValue(b, orderBy);

    if (valueA < valueB) return -1;
    if (valueA > valueB) return 1;
    return 0;
  }

  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClick = (event, partner) => {
    setAnchorEl(event.currentTarget);
    setSelectedPartner(partner);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPartner(null);
  };

  const handleVerificationDocument = (item) => {
    history.push({
      pathname: "/verification-documents-partner",
      state: { data: item },
    });
  };

  const handleApproval = async (row) => {
    const updatedStatus = row?.isSuspended ? false : true;

    sendRequest(
      {
        url: "/partner-approve",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: {
          emailID: localStorage.getItem("user"),
          data: {
            emailID: row.emailID,
            isSuspended: updatedStatus,
          },
        },
      },
      (response) => {
        let parsedResponse;
        try {
          if (typeof response === "string") {
            const jsonStartIndex = response.indexOf("{");
            if (jsonStartIndex !== -1) {
              parsedResponse = JSON.parse(response.substring(jsonStartIndex));
            } else {
              parsedResponse = { message: "Unexpected response format" };
            }
          } else if (typeof response === "object") {
            parsedResponse = response;
          } else {
            parsedResponse = { message: "Unexpected response format" };
          }
        } catch (error) {
          console.log("❌ JSON Parse Error:", error);
          parsedResponse = { message: "Unexpected response format" };
        }

        if (parsedResponse.status === 200 || parsedResponse.status === 201) {
          toast.success(
            parsedResponse.message ||
              `Partner ${
                !updatedStatus ? "approved" : "rejected"
              } successfully!`
          );
          row.isSuspended = !updatedStatus;
          setAnchorEl(false);
          setIsApiCall((prev) => !prev);
        } else {
          console.log("❌ Error Response:", parsedResponse);
          toast.error(parsedResponse.message || "Unexpected error occurred.");
        }
      }
    );
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Paper
          sx={{
            width: "100%",
            mb: 2,
            borderRadius: "10px",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            border: "none",
            borderBottom: "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              pt: 1,
              pb: 1,
              borderRadius: "10px",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 1, md: 0 },
            }}
          >
            <Box component="h2" sx={{ m: 0, display: "flex", alignItems: "center" }}>
              <PortalTitle fallback="Partners" />
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: "12px",
                alignItems: "center",
                flexDirection: { xs: "column", md: "row" },
            }}
          >
            <SearchFilter
              data={partnerData}
              setSearchValue={setSearch}
              searchValue={search}
              setFilteredData={setFilteredData}
            />
            </Box>
          </Box>
          <TableContainer
            component={Paper}
            sx={{
              width: "100%",
              margin: "auto",
              borderRadius: "10px",
              boxShadow: "none",
              border: "none",
              borderBottom: "none",
              "& .MuiPaper-root": {
                border: "none",
                borderBottom: "none",
              },
            }}
          >
            <Table
              sx={{
                fontFamily: "Montserrat",
              }}
              size="medium"
            >
              <TableHead>
                <TableRow>
                  {headers?.map((header) => (
                    <TableCell
                      key={header.key}
                      align="center"
                      sx={{
                        py: 1,
                        px: 2,
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: "rgba(0, 0, 0, 0.87)",
                        width: headerWidths[header.label],
                        verticalAlign: "middle",
                        textAlign: "center",
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === header.key}
                        direction={orderBy === header.key ? order : "asc"}
                        onClick={() => handleSort(header.key)}
                        sx={{ fontSize: "0.875rem" }}
                        hideSortIcon={header.key === "vehicles" || header.key === "drivers" || header.key === "action"}
                      >
                        {header.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={headers?.length}>
                      <Loading datatable="true" />
                    </TableCell>
                  </TableRow>
                ) : filteredData?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={headers?.length}>
                      <div style={{ textAlign: "center", marginTop: "10px" }}>
                        No Data Available
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.875rem", py: 1, px: 2, verticalAlign: "middle", textAlign: "center" }}
                      >
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                          <div style={{ fontSize: "0.875rem", fontWeight: "normal" }}>
                            {item.fullName || "-"}
                          </div>
                          <div style={{ fontSize: "0.75rem", color: "gray", marginTop: "4px" }}>
                            {item.emailID || "-"}
                          </div>
                          <div style={{ fontSize: "0.75rem", color: "gray", marginTop: "2px" }}>
                            {item.mobileNumber || "-"}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.875rem", py: 1, px: 2, verticalAlign: "middle", textAlign: "center" }}
                      >
                        {item.vehicles && item.vehicles.length > 0 ? (
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", flexWrap: "nowrap" }}>
                            <div style={{ fontSize: "0.75rem", textAlign: "center", flex: "1" }}>
                              <div style={{ fontWeight: "500", marginBottom: "2px" }}>
                                {item.vehicles[0].maker} {item.vehicles[0].model}
                              </div>
                              <div style={{ color: "gray", fontSize: "0.7rem" }}>
                                Color: {item.vehicles[0].color || "-"} | Reg: {item.vehicles[0].regNumber || "-"}
                              </div>
                            </div>
                            {item.vehicles.length > 1 && (
                              <div style={{ flexShrink: 0 }}>
                              <CustomInfoIcon
                                message={
                                  <div style={{ fontSize: "12px", padding: "4px", maxWidth: "300px", maxHeight: "350px", overflowY: "auto" }}>
                                    {item.vehicles.map((vehicle, index) => (
                                      <div key={index} style={{ marginBottom: "8px", paddingBottom: "8px", borderBottom: index < item.vehicles.length - 1 ? "1px solid #e0e0e0" : "none" }}>
                                        <div style={{ fontWeight: "600", marginBottom: "4px", color: "#000" }}>
                                          {index + 1}. {vehicle.maker} {vehicle.model}
                                        </div>
                                        <div style={{ color: "#666", fontSize: "11px" }}>
                                          Color: {vehicle.color || "-"} | Reg: {vehicle.regNumber || "-"}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                }
                              />
                              </div>
                            )}
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.875rem", py: 1, px: 2, verticalAlign: "middle", textAlign: "center" }}
                      >
                        {item.drivers && item.drivers.length > 0 ? (
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", flexWrap: "nowrap" }}>
                            <div style={{ fontSize: "0.75rem", textAlign: "center", flex: "1" }}>
                              <div style={{ fontWeight: "500", marginBottom: "2px" }}>
                                {item.drivers[0].fullName || `${item.drivers[0].fName} ${item.drivers[0].lName}`}
                              </div>
                              <div style={{ color: "gray", fontSize: "0.7rem" }}>
                                {item.drivers[0].emailID || "-"}
                              </div>
                              <div style={{ color: "gray", fontSize: "0.7rem" }}>
                                {item.drivers[0].mobileNumber || "-"}
                              </div>
                            </div>
                            {item.drivers.length > 1 && (
                              <div style={{ flexShrink: 0 }}>
                              <CustomInfoIcon
                                message={
                                  <div style={{ fontSize: "12px", padding: "4px", maxWidth: "300px", maxHeight: "350px", overflowY: "auto" }}>
                                    {item.drivers.map((driver, index) => (
                                      <div key={index} style={{ marginBottom: "8px", paddingBottom: "8px", borderBottom: index < item.drivers.length - 1 ? "1px solid #e0e0e0" : "none" }}>
                                        <div style={{ fontWeight: "600", marginBottom: "4px", color: "#000" }}>
                                        {index + 1}. {driver.fullName || `${driver.fName} ${driver.lName}`}
                                        </div>
                                        <div style={{ color: "#666", fontSize: "11px" }}>
                                          {driver.emailID || "-"}
                                        </div>
                                        <div style={{ color: "#666", fontSize: "11px" }}>
                                          {driver.mobileNumber || "-"}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                }
                              />
                              </div>
                            )}
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.875rem", py: 1, px: 2, verticalAlign: "middle", textAlign: "center" }}
                      >
                        {item.city || "-"}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.875rem", py: 1, px: 2, verticalAlign: "middle", textAlign: "center" }}
                      >
                        {item.country || "-"}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: "0.8rem",
                          p: 1,
                          color: !item.isSuspended ? "green" : "red",
                          fontWeight: "bold",
                          verticalAlign: "middle",
                          textAlign: "center",
                        }}
                      >
                        {!item.isSuspended ? "Approved" : "Not Approved"}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(event) => handleMenuClick(event, item)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              background: "white",
              zIndex: 1,
              border: "none !important",
              borderTop: "none !important",
              borderBottom: "none !important",
              "&::before": {
                display: "none",
              },
              "&::after": {
                display: "none",
              },
            }}
          >
            <CommonTablePagination
              page={page}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              setPage={setPage}
              filteredData={search ? filteredPartnerData : partnerData}
              styling={{
                display: "flex",
                justifyContent: "flex-end",
                border: "none",
                borderTop: "none",
                borderBottom: "none",
                "& .MuiTablePagination-root": {
                  border: "none",
                  borderTop: "none",
                  borderBottom: "none",
                  "&::before": {
                    display: "none",
                  },
                  "&::after": {
                    display: "none",
                  },
                },
                "& .MuiTablePagination-toolbar": {
                  border: "none",
                  borderTop: "none",
                  borderBottom: "none",
                  "&::before": {
                    display: "none",
                  },
                  "&::after": {
                    display: "none",
                  },
                },
                "& .MuiTablePagination-spacer": {
                  border: "none",
                },
              }}
            />
          </Box>
        </Paper>
      </Box>

      {/* Menu for actions */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{
          "& .MuiPaper-root": {
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #ccc",
          },
        }}
      >
        <MenuItem onClick={() => handleVerificationDocument(selectedPartner)}>
          <ListItemIcon>
            <VerifiedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Verification Docs</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleApproval(selectedPartner)}>
          <ListItemIcon>
            {!selectedPartner?.isSuspended ? (
              <CancelIcon fontSize="small" style={{ color: "red" }} />
            ) : (
              <CheckCircleIcon fontSize="small" style={{ color: "green" }} />
            )}
          </ListItemIcon>
          <ListItemText
            primary={!selectedPartner?.isSuspended ? "Reject" : "Approve"}
            primaryTypographyProps={{
              style: {
                color: !selectedPartner?.isSuspended ? "red" : "green",
                fontWeight: "bold",
              },
            }}
          />
        </MenuItem>
      </Menu>
    </React.Fragment>
  );
};

export default Partners;
